import {
  createContext,
  useContext,
  useState,
  type PropsWithChildren,
} from "react";

import { UnderwritingReasonsModal } from "@covertree/new-business-uw-decision/src/modals/UnderwritingReasonsModal";
import { UnderwritingDecision } from "@covertree/new-business-graphql";
import { useGetPolicyWithUwResultLazyQuery } from "@covertree/agent-portal-graphql";

interface UWReasonModalState {
  isModalOpen: boolean;
  policyLocator?: string;
}

interface UWReasonModalActions {
  showUWReasonModal: (policyLocator: string) => void;
  closeUWReasonModal: () => void;
}

const initialModalState: UWReasonModalState = {
  isModalOpen: false,
  policyLocator: undefined,
};

const UWReasonContext = createContext<
  UWReasonModalState & UWReasonModalActions
>({
  ...initialModalState,
  showUWReasonModal: () => {},
  closeUWReasonModal: () => {},
});

export const useUWReason = () => useContext(UWReasonContext);

export function UWReasonProvider({ children }: PropsWithChildren) {
  const [modalState, setModalState] =
    useState<UWReasonModalState>(initialModalState);

  const [getPolicy, { data }] = useGetPolicyWithUwResultLazyQuery({
    fetchPolicy: "network-only",
  });

  const showUWReasonModal = (policyLocator: string) => {
    setModalState({
      isModalOpen: true,
      policyLocator,
    });
    getPolicy({ variables: { policyLocator } });
  };

  const closeUWReasonModal = () => {
    setModalState(initialModalState);
  };

  return (
    <UWReasonContext.Provider
      value={{
        ...modalState,
        showUWReasonModal,
        closeUWReasonModal,
      }}>
      {children}
      <UnderwritingReasonsModal
        open={modalState.isModalOpen}
        onClose={closeUWReasonModal}
        reasons={data?.getPolicy?.underwritingNotes || []}
        status={UnderwritingDecision.None}
      />
    </UWReasonContext.Provider>
  );
}
